/**
 * Which findings lead each Performance tab, and which wait behind "More".
 *
 * WHY THIS EXISTS. On the 7 Oct IDFC export the Diagnostics feed held 134
 * cards: 51 "<entity> is not serving", 40 blocking issues (most of them "N of M
 * asset groups have Poor/Average Ad Strength"), 17 per-keyword negative
 * conflicts, 13 campaign health reads and 9 account-level reviews. 29 of the
 * 51 "not serving" entities had `can_deliver: true`, and the Germany asset
 * group Overseas_Transfers — 575 impressions, ₹648 — was serving; it just had
 * no video. A reader who runs the account met a wall of entity detail before
 * the handful of findings that change what the account does. Optimization and
 * Budget & pacing had the same shape: sitelink chores, Google ad-strength
 * nags and TOO_SOON_TO_JUDGE reads ahead of anything with a number on it.
 *
 * So every card on the three agent tabs gets a tier, deterministically, from
 * fields the card and its runs already carry. MAIN cards are the feed and every
 * count — the tab badges, the Summary health strip, the workspace summary.
 * MORE cards sit behind one "More (N)" button per tab that carries its own
 * count. Nothing is dropped: a MORE card keeps its Dismiss, Apply and body.
 *
 * ONE FUNCTION, BY ROUTE. `findingTier(card, ctx)` → `{ tier, reason }`, then
 * the size override; `tierCards(cards, ctx)` adds the rules that need the whole
 * list (one account review shown once, three recommendations per campaign) and
 * is what every surface counts with. Hidden turn-on cards and any other route
 * are MAIN — they keep the behaviour they had.
 *
 * ── DIAGNOSTICS ─────────────────────────────────────────────────────────────
 *   MAIN  campaign-level reads: a stalled campaign, the measurement check, and
 *         a DEGRADED campaign whose run QUANTIFIES a material gap
 *         (diagnosisFindings.healthGap: impression share lost to budget or
 *         rank ≥ 20%, spend under 80% of budget while Google reports it
 *         budget-constrained, or a numeric gap the health block states).
 *         Or, from the runs (campaignGap — what PMax can show): budget-capped
 *         on ≥ half the measured days, or spend < 80% of budget while Google
 *         reports BUDGET_CONSTRAINED. The gap goes into the title.
 *   MORE  a DEGRADED campaign with no measured gap.
 *   MAIN  account-level consolidated findings (`tracking`) — each shown ONCE:
 *         the same review attached to three campaigns leads once, and its
 *         copies wait in More.
 *   MORE  a blocking issue about never-served "(n)" copies only.
 *   MORE  a blocking issue about a single keyword ("Negative keyword conflict
 *         blocks 'hdfc nri savings account'"), whatever its severity, or about
 *         single-ad fragility below HIGH ("All 12 ad groups have only one ad").
 *   MAIN  a blocking issue with HIGH severity (this wins over single-ad
 *         fragility), or one that names a policy / certification /
 *         verification limit in any wording (any severity).
 *   MAIN  a traffic issue (budget, negatives, rank, quality, impression share,
 *         search volume) on a campaign losing ≥ 20% of impressions to budget
 *         or rank — a measured gap.
 *   MORE  a blocking issue with MEDIUM or LOW severity.
 *   MORE  a per-keyword negative-keyword conflict.
 *   MORE  any keyword-level card (diagnosis row or suggestion).
 *   For an entity (asset group, ad group, ad) diagnosis row:
 *   MAIN    verified not serving, and nothing else in the campaign serves.
 *   MORE    limited but serving (`serving: 'limited'`, soft blockers, rarely
 *           served, weak ad strength, single eligible ad, missing recommended
 *           assets such as video).
 *   MORE    never served: zero spend and impressions beside entities that do.
 *   MAIN    verified not serving with ≥10% of its campaign's spend.
 *   MORE    verified not serving below 10% of campaign spend.
 *   MAIN    verified not serving, share unknown — fail visible.
 *
 * ── OPTIMIZATION (route `recommendations`) ──────────────────────────────────
 *   In this order:
 *   MORE  quality gates (failedQualityGate): judged unsupported, suppressed
 *         by the judge or a quality guard, an edit a guard withheld. These are
 *         never promoted by size.
 *   MORE  category demotions, one-click applyable or not: Google maintenance
 *         (customer-match refresh, ad strength, dynamic image opt-in);
 *         extensions (sitelinks, callouts, structured snippets);
 *         observation-mode audiences; final URL expansion whose only stated
 *         benefit is spend or volume; keyword-level items.
 *   MAIN  one-click applyable (`auto_applicable`, the sem_edit payload).
 *   MAIN  judged SUPPORTED and about measurement or the bid signal
 *         (isMeasurementCard: tracking / measurement levers, thin bid signal,
 *         signal stopped or suspected gap, outcome-zero on the campaign's own
 *         bid action, bid-goal mismatch) — no impact estimate required: when
 *         the signal is broken nothing else on the campaign can be estimated.
 *   MORE  search-term negatives saving less than NEGATIVE_MIN_DAILY_SHARE of
 *         the account's daily spend (≈ ₹2,000 over 14 days at IDFC).
 *   MORE  no impact estimate.
 *   MAIN  judged supported, with a stated money or lead impact.
 *   MORE  everything else (not judged supported).
 *   Then at most RECS_PER_CAMPAIGN MAIN per campaign, by estimated impact; the
 *   rest wait in More.
 *
 * ── BUDGET & PACING ─────────────────────────────────────────────────────────
 *   MAIN  the campaign's projected variance is ≥ 30% either way.
 *   MAIN  budget-capped (≥ half the measured days at the cap) with a measured
 *         gap (impression share lost to budget ≥ 20%).
 *   MORE  everything else on a campaign with a pacing read: TOO_SOON_TO_JUDGE,
 *         NOT_COMPUTED, on track, and reads with no figures.
 *   MAIN  no pacing read for the campaign at all — fail visible.
 *
 * ── THE SIZE OVERRIDE (Diagnostics and Optimization) ────────────────────────
 * Applied last. A MORE card that passed the quality gates is promoted back to
 * MAIN when what it touches is big:
 *   • its daily spend at stake ≥ OVERRIDE.ACCOUNT_SHARE (1%) of the account's
 *     daily spend; or
 *   • it affects ≥ OVERRIDE.CAMPAIGN_SHARE (50%) of a campaign whose daily
 *     spend is ≥ OVERRIDE.CAMPAIGN_MIN_ACCOUNT_SHARE of the account's
 *     (≈ ₹2,000/day at IDFC's ₹2.87 lakh/day).
 * Spend at stake is the backend's `spend_at_stake_daily` when the item has it
 * (#3638 — on the diagnosis row, or matched back to its analysis row by entity
 * id or issue text), else the entity's share of campaign spend × the
 * campaign's recent daily spend. A blocking issue gets no share fallback:
 * summing its groups' shares turns "8 of 12 asset groups have Average ad
 * strength" into "affects most of the campaign". Never the row's raw `cost`: on the 7 Oct
 * export it covers a longer window than 14 days (HDFC reads ~₹70,943/day on a
 * ₹4,800 budget). A promoted card says how much in its title: "… · ₹15,300/day
 * affected". A campaign-level suggestion has no share to read, so it is
 * promoted only on a backend figure — "affects the whole campaign" would
 * otherwise promote every sitelink on every large campaign.
 *
 * UNKNOWN FAILS VISIBLE: a card this module does not recognise is MAIN, and
 * nothing is logged.
 *
 * ON "SPEND SHARE". The diagnosis carries the reporting window's
 * `share_of_campaign_spend`; diagnosisFindings.js falls back to the entity's
 * cost over the campaign's summed entity cost (a ratio of one window, never an
 * absolute) when it is absent.
 */
import { entityNotServing, parseShare } from './diagnosisFindings';
import { sortByEstimatedImpact } from './performanceV2';

export { parseShare };

export const TIER = { MAIN: 'main', MORE: 'more' };

/** The MAIN bar for a not-serving entity, as a fraction of campaign spend. */
export const MATERIAL_SHARE = 0.10;

/**
 * The size override, relative to the account so it generalises. The IDFC
 * figures in comments are the 7 Oct export's (₹2.87 lakh/day across campaigns).
 */
export const OVERRIDE = {
  // ≈ ₹2,900/day at IDFC.
  ACCOUNT_SHARE: 0.01,
  // Half a campaign…
  CAMPAIGN_SHARE: 0.5,
  // …that spends at least ≈ ₹2,000/day at IDFC.
  CAMPAIGN_MIN_ACCOUNT_SHARE: 0.007,
};

/** ≈ ₹2,000 over 14 days at IDFC, as a share of the account's daily spend. */
export const NEGATIVE_MIN_DAILY_SHARE = 0.0005;
export const NEGATIVE_WINDOW_DAYS = 14;

/** MAIN Optimization cards per campaign. */
export const RECS_PER_CAMPAIGN = 3;

/** Pacing: projected variance that leads, either way; capped-days share; IS lost. */
export const PACING = { VARIANCE: 0.30, CAPPED_DAYS_SHARE: 0.5, BUDGET_LOST_IS: 0.20 };

const upper = (v) => String(v ?? '').trim().toUpperCase();
const lower = (v) => String(v ?? '').trim().toLowerCase();
const text = (v) => (typeof v === 'string' ? v : (v === null || v === undefined ? '' : String(v)));

// Any limited-by-policy wording: "limited by a policy", "Limited, likely due
// to a policy issue", "policy requiring verification", certificates.
const CAMPAIGN_POLICY_LIMIT = /financial services verification|advertiser verification|certificat|limited[^.]{0,80}\bpolic(y|ies)\b|\bpolic(y|ies)\b[^.]{0,60}\blimit/i;
// An issue about traffic the campaign is not getting: budget, negatives
// blocking keywords, ad rank or quality, impression share, search volume.
const TRAFFIC_ISSUE = /budget|negative|\bblock|\brank\b|quality score|impression share|rarely served|search volume/i;
// "All 12 ad groups have only one ad", "relies on a single approved RSA".
const SINGLE_AD = /\b(only|just)\s+(one|a single|1)\b[^.]*\b(ads?|rsas?)\b|\bsingle\s+(eligible\s+|approved\s+|active\s+)*(ads?|rsas?)\b/i;

const num = (v) => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const main = (reason) => ({ tier: TIER.MAIN, reason });
const more = (reason) => ({ tier: TIER.MORE, reason });

/** Quoted terms in a sentence: 'x', "x", ‘x’, “x”. */
function quoted(str) {
  const out = [];
  const re = /'([^']+)'|"([^"]+)"|‘([^’]+)’|“([^”]+)”/g;
  let m = re.exec(str);
  while (m) {
    out.push(m[1] || m[2] || m[3] || m[4]);
    m = re.exec(str);
  }
  return out;
}

// ── diagnostics ──────────────────────────────────────────────────────────────

function entityTier(card) {
  const e = card.raw || {};
  const ctx = card.entityDelivery || {};
  const notServing = typeof ctx.notServing === 'boolean' ? ctx.notServing : entityNotServing(e);
  if (lower(card.tt) === 'keyword' || lower(e.entity_type) === 'keyword') {
    return more('keyword-level finding');
  }
  if (notServing && ctx.otherServing === false) {
    return main('not serving, and nothing else in the campaign serves');
  }
  if (!notServing) return more('serving with limits');
  if (num(e.cost) === 0 && num(e.impressions) === 0) {
    return more('never served (no spend, no impressions)');
  }
  const share = ctx.share ?? parseShare(e.share_of_campaign_spend);
  if (share === null || share === undefined) return main('not serving; spend share unknown');
  if (share >= MATERIAL_SHARE) return main('not serving, ≥10% of campaign spend');
  return more('not serving, below 10% of campaign spend');
}

/**
 * A blocking issue about traffic the campaign is losing (budget, negatives,
 * rank, a policy limit) — the issues whose named spend is spend at stake. Not
 * single-ad fragility, a single keyword, or ad strength: summing the groups
 * those list turns "8 of 12 asset groups have Average ad strength" into
 * "affects most of the campaign".
 */
function trafficIssue(card) {
  const issue = text(card.raw?.issue ?? card.head);
  if (SINGLE_AD.test(issue) || (/\bkeyword\b/i.test(issue) && quoted(issue).length === 1)) return false;
  return TRAFFIC_ISSUE.test(issue) || CAMPAIGN_POLICY_LIMIT.test(issue);
}

/**
 * A DEGRADED campaign's gap from the runs rather than its diagnosis — what
 * Performance Max can show, where impression share is Search-only:
 * budget-capped on ≥ half the measured days (pacing run), or recent spend
 * under 80% of the daily budget while Google reports BUDGET_CONSTRAINED.
 * Returns reader text, or null.
 */
export function campaignGap(card, ctx) {
  const p = ctx?.pacing?.[card.camp];
  if (p && p.cappedShare !== null && p.cappedShare !== undefined && p.cappedShare >= PACING.CAPPED_DAYS_SHARE) {
    return p.daysCapped && p.daysMeasured
      ? `budget-capped on ${p.daysCapped} of ${p.daysMeasured} days`
      : `budget-capped on ${Math.round(p.cappedShare * 100)}% of days`;
  }
  const budget = num(ctx?.dailyBudget?.[card.camp]);
  const spend = num(ctx?.campaignDaily?.[card.camp]);
  const constrained = (card.servingReasons || []).map(upper).includes('BUDGET_CONSTRAINED');
  if (constrained && budget > 0 && spend > 0 && spend < 0.8 * budget) {
    return `spending ${Math.round((spend / budget) * 100)}% of its daily budget`;
  }
  return null;
}

function issueTier(card, ctx) {
  const sev = upper(card.raw?.severity);
  const issue = text(card.raw?.issue ?? card.head);
  if (card.issueScope?.copiesNeverServed) return more('about never-served "(n)" copies only');
  // One keyword: the issue names "keyword" (singular) and quotes exactly one term.
  if (/\bkeyword\b/i.test(issue) && quoted(issue).length === 1) return more('about a single keyword');
  // HIGH wins over single-ad fragility: the run judged THIS campaign's one ad
  // to be a high risk (UAE Brand BM: its only ad group's only ad).
  if (sev === 'HIGH') return main('campaign-level, high severity');
  if (SINGLE_AD.test(issue)) return more('single-ad fragility');
  if (CAMPAIGN_POLICY_LIMIT.test(issue)) return main('campaign-level policy or verification limit');
  // A traffic issue on a campaign that measurably loses ≥20% of impressions to
  // budget or rank: the gap is measured, so the issue leads.
  const lost = Math.max(num(ctx?.budgetLostIS?.[card.camp]) ?? 0, num(ctx?.rankLostIS?.[card.camp]) ?? 0);
  if (TRAFFIC_ISSUE.test(issue) && lost >= PACING.BUDGET_LOST_IS) {
    return main(`traffic issue, ${Math.round(lost * 100)}% of impressions lost`);
  }
  if (sev === 'MEDIUM' || sev === 'LOW') return more(`campaign-level, ${sev.toLowerCase()} severity`);
  return main('blocking issue, severity unknown');
}

function diagnosticsTier(card, ctx) {
  const action = lower(card.action);
  if (action === 'delivery_health') {
    const verdict = upper(card.raw?.verdict);
    if (verdict !== 'DEGRADED') return main('campaign delivery health');
    if (card.healthGap?.material) return main('degraded, with a measured material gap');
    const gap = campaignGap(card, ctx);
    return gap ? { ...main('degraded, with a measured gap in its runs'), gapText: gap } : more('degraded, with no measured gap');
  }
  if (action === 'measurement_check') return main('campaign measurement');
  if (action === 'tracking') return main('account-level review');
  if (action === 'blocking_issue') return issueTier(card, ctx);
  if (action === 'negative_keyword_conflict') return more('per-keyword negative conflict');
  if (card.fromDiagnosis && (action === 'delivery_blocked' || action === 'delivery_waiting')) {
    return entityTier(card);
  }
  // A suggestion card the run routed here: it leads unless it targets a keyword.
  if (lower(card.tt) === 'keyword') return more('keyword-level finding');
  return main(card.fromDiagnosis ? 'unclassifiable diagnosis' : 'run suggestion');
}

// ── optimization ─────────────────────────────────────────────────────────────

const MONEY_METRIC = /spend|cost|value|revenue|cpa|cpc|wasted|saving|inr|budget/i;
const LEAD_METRIC = /conversion|lead|application|sign.?up|account|enrol|registration/i;

/** The card's stated impact: `{ metric, value }`, or null when it states none. */
function statedImpact(card, ctx) {
  const raw = card.raw || {};
  const gain = raw.expected_gain || raw.scoring?.expected_lift || raw.expected_lift || {};
  const value = gain.status === 'unknown' ? null : (num(gain.absolute_per_month) ?? num(gain.point_pct) ?? num(card.lp));
  const metric = text(gain.metric || card.lm);
  if (value !== null && value !== 0 && metric) return { metric, value };
  // A budget change on a campaign that measurably loses ≥20% of impressions
  // to budget: the impression share lost is the impact basis.
  const lost = num(ctx?.budgetLostIS?.[card.camp]);
  if (/budget/i.test(`${card.action} ${card.head} ${raw.lever}`) && lost !== null && lost >= PACING.BUDGET_LOST_IS) {
    return { metric: 'impression share lost to budget', value: lost, measured: true };
  }
  return null;
}

/** A search-term negative's cost, from its fields or its headline ("costing INR 12,293"). */
function negativeCost(card) {
  const raw = card.raw || {};
  const direct = num(raw.target_metrics?.cost ?? raw.target_metrics?.spend ?? raw.wasted_spend);
  if (direct !== null) return direct;
  const m = /cost(?:ing|s)?\s+(?:[A-Z]{3}|[₹$€£])\s?([\d,]+(?:\.\d+)?)/i.exec(text(card.head || raw.headline));
  return m ? num(m[1].replace(/,/g, '')) : null;
}

/**
 * A card that failed a quality gate: judged unsupported, suppressed by the
 * judge or a quality guard, or an edit a guard withheld. MORE, and never
 * promoted by size — spend at stake makes a sound finding more important; it
 * does not make an unsound one worth reading first.
 */
export function failedQualityGate(card) {
  const raw = card?.raw || {};
  const googleReview = card?.origin === 'Google' && lower(card?.tt) === 'google recommendation';
  const judge = lower(raw.judge_verdict ?? raw._judge_verdict?.verdict ?? (googleReview ? '' : card?.judge));
  if (judge === 'unsupported') return 'judged unsupported';
  if (raw.quality_suppressed || raw._quality_suppressed || raw._suppressed_reason || card?.suppressedReason) {
    return 'suppressed by a quality guard';
  }
  if (raw._withheld_operation || /\b(withheld|guard)\b/i.test(text(raw.advisory_reason))) {
    return 'edit withheld by a guard';
  }
  return null;
}

/**
 * A card about measurement or the bid signal: a tracking / measurement lever,
 * a thin bid signal, a signal that stopped or a suspected gap, zero outcomes
 * on the campaign's own bid action, or a bid-goal mismatch.
 */
const MEASUREMENT_LEVER = /\b(tracking|measurement|conversion_?(tracking|goal|action|signal)\w*|bid_?signal\w*)\b/i;
const MEASUREMENT_TEXT = new RegExp([
  'conversion tracking', 'tracking (tag|label|coverage)', 'thin (bid )?signal', 'signal (is )?too thin',
  'too thin for (effective )?optimi[sz]ation', 'signal (stopped|gap)', 'suspected gap',
  'bid(ding)?[- ]goal mismatch', 'outcome[- ]?zero', 'zero (conversions|outcomes) on (the|its) (own )?bid action',
  'primary (conversion )?action', 'optimi[sz]ing (for|toward)',
].join('|'), 'i');
export function isMeasurementCard(card) {
  const raw = card?.raw || {};
  const levers = [card?.action, card?.cat, raw.lever, raw.category, raw.action_type].map(text).join(' ');
  if (MEASUREMENT_LEVER.test(levers)) return true;
  return MEASUREMENT_TEXT.test([card?.head, card?.plain, raw.headline, raw.plain_summary].map(text).join(' '));
}

const isPauseCard = (card) => /\bpause\b/i.test([card?.head, card?.raw?.lever, card?.raw?.title].map(text).join(' '))
  || /^pause$/i.test(text(card?.raw?.lever));

function recommendationTier(card, ctx) {
  const raw = card.raw || {};
  const action = lower(card.action);
  const words = [card.head, card.plain, raw.lever_label, raw.recommended_value, card.rec]
    .map(text).join(' ');
  const googleReview = card.origin === 'Google' && lower(card.tt) === 'google recommendation';
  const judge = lower(raw.judge_verdict ?? raw._judge_verdict?.verdict ?? (googleReview ? '' : card.judge));

  // 1. Quality gates. Never promoted (findingTier reads `gated`).
  const gate = failedQualityGate(card);
  if (gate) return { ...more(gate), gated: true };
  // 2. Category demotions — applyable or not: one click is not importance.
  if (googleReview && /customer.?match|ad strength|dynamic image/i.test(`${words} ${action}`)) {
    return more('Google maintenance');
  }
  if (/extension|callout|sitelink|snippet/i.test(`${action} ${lower(card.cat)}`)
    || /\b(sitelinks?|callouts?|structured snippets?)\b/i.test(text(card.head))) {
    return more('extension, callout or sitelink');
  }
  if (/observation/i.test(words) && /audience/i.test(`${action} ${words}`)) {
    return more('observation-mode audience');
  }
  const impact = statedImpact(card, ctx);
  if (/final.?url.?expansion/i.test(`${action} ${words}`)
    && (!impact || /spend|cost|impression|click|reach|traffic/i.test(impact.metric))) {
    return more('final URL expansion, spend or volume only');
  }
  if (lower(card.tt) === 'keyword') return more('keyword-level item');
  // 3. What leads: one-click applyable; a SUPPORTED measurement or bid-signal
  // finding, with or without an estimate (when the signal the bidder
  // optimises on is broken, nothing else on the campaign can be estimated, so
  // requiring an estimate inverts the priority); or judged supported with a
  // stated money or lead impact.
  if (card.auto) return main('one-click applyable');
  // Measurement / bid signal, judged supported — or not judged at all, on a
  // platform with no judge for it (Meta). An unjudged PAUSE stays behind:
  // pausing on a signal nobody verified is the action the signal can't support.
  const unjudged = !googleReview && !judge && !raw._account_review_scope;
  if (isMeasurementCard(card) && (judge === 'supported' || (unjudged && !isPauseCard(card)))) {
    return main(judge === 'supported' ? 'supported measurement or bid-signal finding' : 'unjudged measurement or bid-signal finding');
  }
  if (/negative/i.test(action)) {
    const cost = negativeCost(card);
    const bar = ctx?.accountDaily > 0 ? NEGATIVE_MIN_DAILY_SHARE * ctx.accountDaily : null;
    if (cost !== null && bar !== null && cost / NEGATIVE_WINDOW_DAYS < bar) {
      return more('search-term negative below the size bar');
    }
  }
  if (!impact) return more('no impact estimate');
  // Google's own review accepting the recommendation counts as support.
  const supported = judge === 'supported' || (googleReview && ['adopt', 'adapt'].includes(lower(card.judge)));
  if (supported && impact.measured) return main('supported, with impression share lost to budget');
  if (supported && (MONEY_METRIC.test(impact.metric) || LEAD_METRIC.test(impact.metric))) {
    return main('judged supported, with a stated money or lead impact');
  }
  return more(supported ? 'supported, impact not in money or leads' : 'not judged supported');
}

// ── pacing ───────────────────────────────────────────────────────────────────

function pacingTier(card, ctx) {
  const p = ctx?.pacing?.[card.camp];
  if (!p) return main('no pacing read for the campaign');
  if (p.variance !== null && Math.abs(p.variance) + 1e-9 >= PACING.VARIANCE) {
    return main(`projected variance ${Math.round(p.variance * 100)}%`);
  }
  const lostIS = ctx?.budgetLostIS?.[card.camp];
  if (p.cappedShare !== null && p.cappedShare >= PACING.CAPPED_DAYS_SHARE
    && lostIS !== null && lostIS !== undefined && lostIS >= PACING.BUDGET_LOST_IS) {
    return main('budget-capped, with impression share lost to budget');
  }
  if (p.status === 'TOO_SOON_TO_JUDGE' || p.status === 'NOT_COMPUTED') return more(p.status);
  if (/ON.?TRACK/.test(p.status)) return more('on track');
  return more('no measured pacing gap');
}

// ── the size override ────────────────────────────────────────────────────────

/** Daily spend this card touches, and the share of its campaign: `{ daily, share }`. */
export function spendAtStake(card, ctx) {
  const raw = card.raw || {};
  const campDaily = num(ctx?.campaignDaily?.[card.camp]);
  // An entity's own share; a blocking issue's whole campaign (when it is about
  // the campaign as a whole) or the summed share of the entities it names.
  let share = card.entityDelivery?.share ?? null;
  if (share === null && card.issueScope && trafficIssue(card)) {
    share = card.issueScope.wholeCampaign ? 1 : (card.issueScope.share ?? null);
  }
  if (share === null && card.tid && ctx?.entityShare) {
    share = ctx.entityShare[`${card.camp}::${String(card.tid).toLowerCase()}`] ?? null;
  }
  let daily = num(raw.spend_at_stake_daily);
  if (daily === null && card.fromDiagnosis) {
    if (card.action === 'blocking_issue') {
      daily = num(ctx?.stakeByIssue?.[`${card.camp}::${lower(raw.issue).replace(/\s+/g, ' ')}`]);
    } else {
      const keys = [raw.entity_id, raw.asset_group_id, raw.ad_group_id, raw.entity_name, raw.asset_group_name]
        .filter((k) => k !== null && k !== undefined && text(k).trim());
      daily = keys.map((k) => num(ctx?.stakeByEntity?.[`${card.camp}::${text(k).trim().toLowerCase()}`]))
        .find((v) => v !== null && v !== undefined) ?? null;
    }
  }
  if (daily === null && share !== null && campDaily !== null) daily = share * campDaily;
  if (share === null && daily !== null && campDaily > 0) share = Math.min(1, daily / campDaily);
  return { daily, share, campDaily };
}

function sizeOverride(card, ctx) {
  const account = num(ctx?.accountDaily);
  if (!(account > 0)) return null;
  const { daily, share, campDaily } = spendAtStake(card, ctx);
  if (daily !== null && daily >= OVERRIDE.ACCOUNT_SHARE * account) {
    return { reason: 'affects ≥1% of account daily spend', daily };
  }
  if (share !== null && share >= OVERRIDE.CAMPAIGN_SHARE
    && campDaily !== null && campDaily >= OVERRIDE.CAMPAIGN_MIN_ACCOUNT_SHARE * account) {
    return { reason: 'affects ≥50% of a material campaign', daily };
  }
  return null;
}

/**
 * The tier of one card: `{ tier, reason, promoted?, daily? }`.
 *
 * `reason` is for tests and devtools; the page does not print it.
 */
export function findingTier(card, ctx = {}) {
  if (!card || typeof card !== 'object') return main('unclassifiable');
  // A strategist card arrives already judged: the review rated its importance at campaign,
  // market and account level, and material at any one of them leads (strategistCards.js).
  if (card.strategist) {
    return card.strategist.material ? main('strategist review: material') : more('strategist review: not material at any level');
  }
  const materiality = card.raw?.materiality;
  let base;
  if (card.route === 'diagnostics') base = diagnosticsTier(card, ctx);
  else if (card.route === 'recommendations') base = recommendationTier(card, ctx);
  else if (card.route === 'pacing') base = pacingTier(card, ctx);
  else return main('not a tiered route');
  if (materiality?.version === 1 && materiality.status === 'watch' && !base.gated) base = more(materiality.reason);
  if (materiality?.version === 1 && !base.gated) {
    const exposure = spendAtStake(card, ctx);
    const levels = materiality.assessment?.levels || {};
    const level = ['account', 'market', 'campaign'].find(l => levels[l]?.material === true && levels[l]?.reason);
    if (level && exposure.daily > 0) return { ...main(levels[level].reason), level, daily: exposure.daily };
    if (['account', 'market', 'campaign'].every(l => levels[l]?.material === false && levels[l]?.reason)) {
      return more('Contextual review found no material effect at campaign, market or account level');
    }
  }
  // 4. The size override, last, and never for a card that failed a quality gate.
  if (base.tier === TIER.MORE && card.route !== 'pacing' && !base.gated) {
    const up = sizeOverride(card, ctx);
    if (up) return { tier: TIER.MAIN, reason: `${up.reason} (was: ${base.reason})`, promoted: true, daily: up.daily };
  }
  return base;
}

// ── list-level rules, and what every surface counts with ─────────────────────

const SYMBOL = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$', NZD: 'NZ$', SGD: 'S$' };

/** "₹15,300" — rounded, grouped by hand so no runtime locale is involved. */
export function formatDaily(value, ccy) {
  const step = value >= 1000 ? 100 : 10;
  const n = Math.round(value / step) * step;
  const grouped = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const code = /^[A-Z]{3}$/.test(ccy || '') ? ccy : null;
  return `${code ? (SYMBOL[code] || `${code} `) : ''}${grouped}`;
}

const humanize = (v) => text(v).replace(/[_-]+/g, ' ').trim();

/** The title a card with no headline gets, instead of an empty one. */
export function fallbackHead(card) {
  const label = text(card.raw?.lever_label).trim() || humanize(card.action) || 'Recommendation';
  const what = label.charAt(0).toUpperCase() + label.slice(1);
  const target = text(card.tn).trim();
  return target ? `${what} — ${target}` : what;
}

/**
 * Tier a list: `{ main, more, tierOf }`, order preserved.
 *
 * On top of findingTier: a card with no headline gets fallbackHead; an
 * account-level review leads once however many campaigns carry it; at most
 * RECS_PER_CAMPAIGN Optimization cards lead per campaign, by estimated impact
 * (a promoted card is exempt — size is why it leads); and a promoted card's
 * title says how much it touches. Cards that change are copies; the rest are
 * the same objects.
 */
export function tierCards(cards = [], ctx = {}) {
  // No headline AND no summary: RecCard would title it "<action> on <target>".
  // A card with a summary keeps RecCard's labelled summary fallback.
  const list = (Array.isArray(cards) ? cards : []).map((c) => (c && c.route !== 'hidden'
    && !text(c.head).trim() && !text(c.plain).trim() ? { ...c, head: fallbackHead(c) } : c));
  const tiers = new Map(list.map((c) => [c, findingTier(c, ctx)]));

  const seenReview = new Set();
  list.forEach((c) => {
    if (c.route !== 'diagnostics' || lower(c.action) !== 'tracking') return;
    const key = lower(c.head).replace(/\s+/g, ' ');
    if (seenReview.has(key)) tiers.set(c, more('same account review, already shown'));
    else seenReview.add(key);
  });

  const byCamp = new Map();
  list.forEach((c) => {
    const t = tiers.get(c);
    if (c.route !== 'recommendations' || t.tier !== TIER.MAIN || t.promoted || c.raw?.materiality?.version === 1 || c.strategist) return;
    const k = c.camp || 'account';
    if (!byCamp.has(k)) byCamp.set(k, []);
    byCamp.get(k).push(c);
  });
  byCamp.forEach((group) => {
    sortByEstimatedImpact(group).slice(RECS_PER_CAMPAIGN)
      .forEach((c) => tiers.set(c, more(`beyond the ${RECS_PER_CAMPAIGN} highest-impact for this campaign`)));
  });

  const out = { main: [], more: [], tierOf: new Map() };
  list.forEach((c) => {
    const t = tiers.get(c);
    let card = c;
    if (t.gapText) card = { ...c, head: `${c.head}: ${t.gapText}` };
    if (t.promoted && t.daily !== null && t.daily !== undefined) {
      card = { ...c, head: `${c.head} · ${formatDaily(t.daily, c.ccy || ctx?.currency?.[c.camp] || ctx?.accountCurrency)}/day affected` };
    }
    if (c.raw?.materiality?.version === 1) card = { ...card, materialityPlacement: t.level || null, exposure: spendAtStake(c, ctx), placementReason: t.reason };
    out.tierOf.set(card.id, t);
    out[t.tier === TIER.MORE ? 'more' : 'main'].push(card);
  });
  return out;
}

/** The MAIN cards of a list — what every badge and summary counts. */
export const mainCards = (cards, ctx) => tierCards(cards, ctx).main;

// ── the context: account and campaign spend, pacing reads ────────────────────

const DAY = 86400000;
const dayOf = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text(s));
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/**
 * A campaign's recent daily spend, from one run.
 *
 *   1. the backend's `result.details.analysis.campaign_daily_spend` (#3638)
 *   2. the mean of the last 14 days of `analysis_inputs.window_daily`, over the
 *      days that HAVE data; a series that ended more than 14 days before
 *      `now` reads 0 (not spending now)
 *   3. the pacing rollup's `recent_daily_spend`, when above 0 — it reads 0 on
 *      Search campaigns that plainly spent, so 0 there is "not measured"
 */
export function campaignDailySpend(run, now = Date.now()) {
  const res = run?.result || {};
  const stated = num(res.details?.analysis?.campaign_daily_spend);
  if (stated !== null) return stated;
  const series = (Array.isArray(res.analysis_inputs?.window_daily) ? res.analysis_inputs.window_daily : [])
    .map((r) => ({ t: dayOf(r?.date), spend: num(r?.spend) || 0 }))
    .filter((r) => r.t !== null);
  if (series.length) {
    const last = Math.max(...series.map((r) => r.t));
    // Staleness against today, not the run's date: a stored run of a campaign
    // that has since stopped must not keep counting its old spend.
    if (Number.isFinite(now) && now - last > 15 * DAY) return 0;
    const recent = series.filter((r) => r.t > last - 14 * DAY);
    const days = new Set(recent.map((r) => r.t)).size;
    return days ? recent.reduce((a, r) => a + r.spend, 0) / days : null;
  }
  const rollup = res.analysis_inputs?.pacing_rollup || res.analysis_data?.pacing_rollup || {};
  const rds = num(rollup.recent_daily_spend);
  return rds !== null && rds > 0 ? rds : null;
}

/**
 * Everything the tiering reads across runs, built once per page from the runs
 * usePerformance already holds:
 *
 *   accountDaily   sum of the campaigns' recent daily spend, over `accountRuns`
 *                  — every run the account returned, not the page's campaign
 *                  scope; null when only a drill-down's runs are loaded (the
 *                  account total is unknown there, so nothing is promoted)
 *   accountCurrency the account's most common campaign currency
 *   campaignDaily  { campaignId: recent daily spend }
 *   currency       { campaignId: ISO code }
 *   pacing         { campaignId: { status, variance, cappedShare } } (pacing runs)
 *   budgetLostIS   { campaignId: impression share lost to budget } (diagnosis)
 *   entityShare    { "campaignId::entity id or name": share of campaign spend }
 *   stakeByEntity  { "campaignId::entity id or name": spend_at_stake_daily }  (#3638)
 *   stakeByIssue   { "campaignId::issue text": spend_at_stake_daily }          (#3638)
 */
export function tierContext(runs = [], diagnoses = [], { accountRuns = runs, now = Date.now() } = {}) {
  const ctx = {
    accountDaily: null,
    accountCurrency: null,
    rankLostIS: {},
    dailyBudget: {},
    campaignDaily: {},
    currency: {},
    pacing: {},
    budgetLostIS: {},
    entityShare: {},
    stakeByEntity: {},
    stakeByIssue: {},
  };
  // Newest first; ISO timestamps compare as strings (no locale), and the run id
  // breaks ties so the pick never depends on input order.
  const byNewest = (list) => [...(Array.isArray(list) ? list : [])].sort((a, b) => {
    const ta = text(a?.completedAt);
    const tb = text(b?.completedAt);
    if (ta !== tb) return ta < tb ? 1 : -1;
    const ia = text(a?.messageId);
    const ib = text(b?.messageId);
    return ia < ib ? -1 : (ia > ib ? 1 : 0);
  });
  const newest = byNewest(runs);
  newest.forEach((r) => {
    const camp = r?.campaignId;
    if (!camp) return;
    const res = r.result || {};
    if (!(camp in ctx.campaignDaily)) {
      const daily = campaignDailySpend(r, now);
      if (daily !== null) ctx.campaignDaily[camp] = daily;
    }
    const ccy = [res.analysis_inputs?.currency_code, res.analysis_data?.campaign_structure?.currency_code,
      res.details?.analysis?.campaign_pacing?.currency_code].find((v) => /^[A-Z]{3}$/.test(text(v)));
    if (ccy && !ctx.currency[camp]) ctx.currency[camp] = ccy;
    const budget = num(res.details?.analysis?.campaign_pacing?.daily_budget
      ?? res.analysis_inputs?.pacing_rollup?.daily_budget);
    if (budget !== null && budget > 0 && !(camp in ctx.dailyBudget)) ctx.dailyBudget[camp] = budget;
    const cp = res.details?.analysis?.campaign_pacing || {};
    const variance = num(cp.projected_variance_pct) ?? (num(cp.pace_index) !== null ? num(cp.pace_index) - 1 : null);
    // Only a run that READ pacing (a status or a variance) counts; a pacing run
    // with no campaign_pacing block leaves the campaign unread — fail visible.
    if (r.analysisKind === 'pacing' && !ctx.pacing[camp] && (cp.pacing_status || variance !== null)) {
      const rollup = res.analysis_inputs?.pacing_rollup || {};
      const cap = cp.budget_cap_behaviour || rollup.budget_cap_behaviour || {};
      ctx.pacing[camp] = {
        status: upper(cp.pacing_status),
        variance,
        cappedShare: num(cap.share_of_days_capped),
        daysCapped: num(cap.days_at_budget_cap),
        daysMeasured: num(cap.days_measured),
      };
    }
    // #3638's per-item spend at stake, on the analysis rows. The diagnosis rows
    // the page renders may or may not carry it, so it is indexed here by
    // entity id/name and by issue text for spendAtStake to match back.
    const analysis = res.details?.analysis || {};
    (Array.isArray(analysis.delivery_diagnosis) ? analysis.delivery_diagnosis : []).forEach((row) => {
      const v = num(row?.spend_at_stake_daily);
      if (v === null) return;
      [row.entity_id, row.asset_group_id, row.ad_group_id, row.entity_name, row.asset_group_name]
        .filter((k) => k !== null && k !== undefined && text(k).trim())
        .forEach((k) => {
          const key = `${camp}::${text(k).trim().toLowerCase()}`;
          if (!(key in ctx.stakeByEntity)) ctx.stakeByEntity[key] = v;
        });
    });
    (Array.isArray(analysis.blocking_issues) ? analysis.blocking_issues : []).forEach((b) => {
      const v = num(b?.spend_at_stake_daily);
      const key = `${camp}::${lower(b?.issue).replace(/\s+/g, ' ')}`;
      if (v !== null && !(key in ctx.stakeByIssue)) ctx.stakeByIssue[key] = v;
    });
  });
  (Array.isArray(diagnoses) ? diagnoses : []).forEach((d) => {
    const camp = d?.campaignId;
    if (!camp) return;
    const lost = num(d.deliveryEvidence?.budgetLostIS);
    if (lost !== null) ctx.budgetLostIS[camp] = lost;
    const rank = num(d.deliveryEvidence?.rankLostIS);
    if (rank !== null) ctx.rankLostIS[camp] = rank;
    (Array.isArray(d.entities) ? d.entities : []).forEach((e) => {
      const share = parseShare(e?.share_of_campaign_spend);
      if (share === null) return;
      [e.entity_id, e.asset_group_id, e.ad_group_id, e.entity_name, e.asset_group_name]
        .filter((k) => k !== null && k !== undefined && text(k).trim())
        .forEach((k) => { ctx.entityShare[`${camp}::${text(k).trim().toLowerCase()}`] = share; });
    });
  });
  // The account total from every run the account returned, so a page scoped to
  // some campaigns and the workspace summary read the same bar.
  const accountDaily = {};
  byNewest(accountRuns).forEach((r) => {
    if (!r?.campaignId || r.campaignId in accountDaily) return;
    const daily = campaignDailySpend(r, now);
    if (daily !== null) accountDaily[r.campaignId] = daily;
  });
  const values = Object.values(accountDaily);
  ctx.accountDaily = values.length ? values.reduce((a, v) => a + v, 0) : null;
  const counts = {};
  Object.values(ctx.currency).forEach((c) => { counts[c] = (counts[c] || 0) + 1; });
  ctx.accountCurrency = Object.keys(counts).sort((a, b) => counts[b] - counts[a] || (a < b ? -1 : 1))[0] || null;
  return ctx;
}

/** True for a card every count includes, judged alone (no list rules). */
export const isMainFinding = (card, ctx) => findingTier(card, ctx).tier === TIER.MAIN;

const findingTierModule = {
  TIER, MATERIAL_SHARE, OVERRIDE, NEGATIVE_MIN_DAILY_SHARE, RECS_PER_CAMPAIGN, PACING,
  parseShare, findingTier, tierCards, mainCards, tierContext, campaignDailySpend, spendAtStake,
  formatDaily, fallbackHead, isMainFinding, failedQualityGate, isMeasurementCard,
};

export default findingTierModule;
